import { RefObject, useLayoutEffect, useState } from "react";
import { useAppShellContext } from "../components/layout/AppShell";

interface BriefStickyNavOptions {
    /** Run the effect only when the brief HTML is rendered. */
    enabled: boolean;
    /** The sanitized HTML injected into the root — re-wires when it changes. */
    contentHtml: string;
    /** Selector for the sidenav anchors inside the root. */
    navLinkSelector: string;
    /** Section id to mark active on mount; defaults to the first nav target. */
    defaultSectionId?: string;
}

/**
 * Sticky-header + scroll-tracked sidenav behavior for injected brief HTML
 * (buyer briefs v2 and the IHG demo). The brief markup ships as a sanitized
 * fragment with no scripts, so the view re-implements the standalone
 * design's behavior here:
 *  - measures the sticky `.brief-header-container` into --brief-header-height
 *    and the antd card head into --brief-sticky-offset, so the header, the
 *    sidenav, and section scroll-margins stack correctly (briefs without the
 *    header container simply measure 0);
 *  - tracks the active section against the app layout's scroll container and
 *    toggles `.active` on the sidenav links;
 *  - smooth-scrolls on sidenav clicks.
 */
export function useBriefStickyNav(
    rootRef: RefObject<HTMLElement | null>,
    { enabled, contentHtml, navLinkSelector, defaultSectionId }: BriefStickyNavOptions
): void {
    const { getContentContainer } = useAppShellContext();
    const [retryTick, setRetryTick] = useState(0);

    // Layout effect so the CSS vars are written before first paint — a paint
    // with stale/absent offsets briefly pins the header and sidenav at the
    // wrong height.
    useLayoutEffect(() => {
        const root = rootRef.current;
        const container = getContentContainer();
        if (!enabled) return undefined;
        if (!root || !container) {
            // `enabled` can flip true a commit before the brief div (or the
            // layout's scroll container) mounts — a ref attaching doesn't
            // re-fire effects, so without a retry the wiring never happens
            // and the sticky offsets stay unset. Poll next frame until both
            // elements exist.
            const raf = requestAnimationFrame(() => setRetryTick((t) => t + 1));
            return () => cancelAnimationFrame(raf);
        }

        const header = root.querySelector<HTMLElement>(".brief-header-container");
        const cardHead = root.closest(".ant-card")?.querySelector<HTMLElement>(".ant-card-head");
        const navLinks = Array.from(root.querySelectorAll<HTMLAnchorElement>(navLinkSelector));
        const sections = navLinks
            .map((link) => {
                const href = link.getAttribute("href");
                return href ? root.querySelector<HTMLElement>(href) : null;
            })
            .filter((section): section is HTMLElement => Boolean(section));

        const stickyOffset = () =>
            cardHead ? Math.ceil(cardHead.getBoundingClientRect().height) : 0;

        const syncHeaderHeight = () => {
            const height = header ? Math.ceil(header.getBoundingClientRect().height) : 0;
            document.documentElement.style.setProperty("--brief-header-height", `${height}px`);
            document.documentElement.style.setProperty(
                "--brief-sticky-offset",
                `${stickyOffset()}px`
            );
        };

        // The card head and the brief's own header are the same visual block
        // but live in different DOM trees (one outside the card body, one
        // inside the injected HTML), so no CSS selector spans them. Publishing
        // whether the brief header exists lets the card head drop its bottom
        // border when one follows, and keep it when the head is the bottom
        // edge of the block. Set once: the injected HTML does not change
        // shape without remounting this effect.
        document.documentElement.style.setProperty(
            "--brief-header-present",
            header ? "1" : "0"
        );

        // A pinned bar reads as pinned only once something passes under it.
        // Toggled here rather than in a new listener because this effect
        // already owns a passive scroll handler on the same container.
        const syncScrolled = () => {
            document.documentElement.style.setProperty(
                "--brief-scrolled",
                container.scrollTop > 0 ? "1" : "0"
            );
        };

        const setActiveSection = (sectionId: string) => {
            navLinks.forEach((link) => {
                link.classList.toggle("active", link.getAttribute("href") === `#${sectionId}`);
            });
        };

        const syncActiveSection = () => {
            // Re-measure every scroll tick (two getBoundingClientRect calls —
            // cheap). Async content (logo image, web fonts, the stock chip)
            // can change either sticky element's height after mount; stale
            // vars pin the title header and sidenav too high, overlapping
            // the element above.
            syncHeaderHeight();
            syncScrolled();
            if (!sections.length) return;
            if (container.scrollTop + container.clientHeight >= container.scrollHeight - 2) {
                setActiveSection(sections[sections.length - 1].id);
                return;
            }
            const containerTop = container.getBoundingClientRect().top;
            const headerHeight = header ? header.getBoundingClientRect().height : 0;
            const activationLine = containerTop + stickyOffset() + headerHeight + 24;
            let activeSection = sections[0];
            sections.forEach((section) => {
                if (section.getBoundingClientRect().top <= activationLine) {
                    activeSection = section;
                }
            });
            setActiveSection(activeSection.id);
        };

        const onNavClick = (event: Event) => {
            event.preventDefault();
            const link = event.currentTarget as HTMLAnchorElement;
            const href = link.getAttribute("href");
            const target = href ? root.querySelector<HTMLElement>(href) : null;
            if (!target) return;
            setActiveSection(target.id);
            target.scrollIntoView({ behavior: "smooth", block: "start" });
        };

        syncHeaderHeight();
        syncScrolled();
        // Fonts load after mount and can nudge both sticky heights.
        document.fonts?.ready?.then(syncHeaderHeight).catch(() => undefined);
        const initialId = defaultSectionId ?? sections[0]?.id;
        if (initialId) setActiveSection(initialId);
        navLinks.forEach((link) => link.addEventListener("click", onNavClick));
        container.addEventListener("scroll", syncActiveSection, { passive: true });
        window.addEventListener("resize", syncHeaderHeight);
        const resizeObserver =
            "ResizeObserver" in window ? new ResizeObserver(syncHeaderHeight) : null;
        if (header) resizeObserver?.observe(header);
        if (cardHead) resizeObserver?.observe(cardHead);

        return () => {
            navLinks.forEach((link) => link.removeEventListener("click", onNavClick));
            container.removeEventListener("scroll", syncActiveSection);
            window.removeEventListener("resize", syncHeaderHeight);
            resizeObserver?.disconnect();
            document.documentElement.style.removeProperty("--brief-header-height");
            document.documentElement.style.removeProperty("--brief-sticky-offset");
            document.documentElement.style.removeProperty("--brief-header-present");
            document.documentElement.style.removeProperty("--brief-scrolled");
        };
    }, [rootRef, enabled, contentHtml, navLinkSelector, defaultSectionId, getContentContainer, retryTick]);
}
